import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  IconButton,
  InputAdornment,
  Link,
  CircularProgress,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from 'shared/contexts/AuthContext';
import { usePageTitle, getPageTitle } from 'shared/hooks/usePageTitle';
import { getDefaultRouteForRole } from 'shared/utils/roleRedirects';
import { useTracking } from 'shared/hooks/useTracking';
import authService, {
  requires2FA,
  requiresCompanySelection,
  hasGracePeriod,
  isAuthenticatorBlocked,
  CompanyOption,
} from 'shared/services/authService';
import TwoFactorVerification, { TwoFactorMethod } from './TwoFactorVerification';
import CompanySelector from './CompanySelector';
import AuthenticatorRequiredScreen from './AuthenticatorRequiredScreen';

// Get version from package.json
const APP_VERSION = process.env.REACT_APP_VERSION || '0.1.0';

// Login step type
type LoginStep = 'credentials' | 'company-select' | '2fa' | 'blocked';

// 2FA State interface (updated for new single-method flow)
interface TwoFactorState {
  required: boolean;
  maskedEmail: string;
  actualEmail: string;
  expiresInMinutes?: number;
  // New single-method properties
  requiredMethod: TwoFactorMethod;
  companyRequiredMethod: TwoFactorMethod;
  userHasTotp: boolean;
  // Grace period info
  isGracePeriod: boolean;
  gracePeriodEndsAt: string | null;
  canUseEmailDuringGrace: boolean;
  // Email error info
  emailError?: boolean;
  hasBackupCodes?: boolean;
  // Company context
  selectedCompanyId?: string;
}

// Company selection state
interface CompanySelectionState {
  email: string;
  companies: CompanyOption[];
}

export const Login: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { completeLoginWithToken, user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { track } = useTracking();

  // An already-authenticated user can land here via back-navigation (the
  // token/session is still valid) — bounce straight to their home instead
  // of showing the login form, which reads as "it logged me out". "/"
  // resolves to the right per-role/company-type route via RoleBasedRedirect.
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Login flow state
  const [step, setStep] = useState<LoginStep>('credentials');
  const [companyState, setCompanyState] = useState<CompanySelectionState | null>(null);
  const [twoFactorState, setTwoFactorState] = useState<TwoFactorState | null>(null);
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  // Get registration data from navigation state
  const registrationState = location.state as { message?: string; email?: string } | null;

  // Set page title based on current step
  const getTitle = () => {
    switch (step) {
      case 'company-select':
        return 'Seleccionar Empresa';
      case '2fa':
        return 'Verificación 2FA';
      case 'blocked':
        return 'Configuración Requerida';
      default:
        return 'Inicio de Sesión';
    }
  };

  usePageTitle(
    getPageTitle(getTitle()),
    'Sistema de Gestión de Seguridad - Inicio de sesión'
  );

  // Pre-fill email if coming from registration
  useEffect(() => {
    if (registrationState?.email) {
      setEmail(registrationState.email);
    }
  }, [registrationState]);

  // Handle login with optional companyId
  const handleLogin = async (companyId?: string) => {
    setError('');
    setIsLoading(true);

    // Track form submission start
    track.formEvent('login_form', 'submit');

    try {
      const response = await authService.login(email, password, companyId);

      // Check if company selection is required
      if (requiresCompanySelection(response)) {
        track.event('company_selection_required', { email: response.email });
        setCompanyState({
          email: response.email,
          companies: response.companies,
        });
        setStep('company-select');
        setIsLoading(false);
        return;
      }

      // Check if 2FA is required
      if (requires2FA(response)) {
        track.event('2fa_required', {
          email: response.email,
          method: response.requiredMethod,
          isGracePeriod: response.isGracePeriod,
        });
        setTwoFactorState({
          required: true,
          maskedEmail: response.twoFactor?.email || response.email,
          actualEmail: email,
          expiresInMinutes: response.expiresInMinutes,
          // New single-method properties
          requiredMethod: (response.twoFactor?.method || response.requiredMethod || 'email') as TwoFactorMethod,
          companyRequiredMethod: (response.companyRequiredMethod || response.requiredMethod || 'email') as TwoFactorMethod,
          userHasTotp: response.userHasTotp || false,
          // Grace period info
          isGracePeriod: response.isGracePeriod || false,
          gracePeriodEndsAt: response.gracePeriodEndsAt || null,
          canUseEmailDuringGrace: response.canUseEmailDuringGrace || false,
          // Email error info
          emailError: response.emailError || false,
          hasBackupCodes: response.hasBackupCodes || false,
          // Company context
          selectedCompanyId: response.selectedCompanyId || companyId,
        });
        setStep('2fa');
        setIsLoading(false);
        return;
      }

      // Direct login success (no 2FA required)
      const directResponse = response as { token: string; data?: any; user?: any; gracePeriod?: any };
      authService.setToken(directResponse.token);

      // Check for grace period and store in session storage for dashboard to display
      if (hasGracePeriod(response)) {
        sessionStorage.setItem('gracePeriodWarning', JSON.stringify(directResponse.gracePeriod));
        track.event('login_grace_period', {
          daysRemaining: directResponse.gracePeriod?.daysRemaining,
        });
      }

      // Complete login using the stored token
      const user = await completeLoginWithToken();

      // Track successful login
      track.auth('login', 'email');
      track.event('login_success', {
        user_role: user.role,
        company: user.company?.name || 'unknown',
        timestamp: new Date().toISOString(),
      });

      // Redirect to default route
      const defaultRoute = getDefaultRouteForRole(user.role);
      navigate(defaultRoute, { replace: true });
    } catch (err: unknown) {
      // Check for authenticator blocked error
      if (isAuthenticatorBlocked(err)) {
        track.event('login_blocked_authenticator_required');
        setStep('blocked');
        setIsLoading(false);
        return;
      }

      // Track login error
      const errorMessage = err instanceof Error ? err.message : 'Email o contraseña incorrectos';
      track.error('login_failed', errorMessage);
      track.formEvent('login_form', 'error', undefined, [errorMessage]);

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleLogin();
  };

  const handleCompanySelect = async (companyId: string) => {
    track.event('company_selected', { companyId });
    await handleLogin(companyId);
  };

  const handleVerify2FA = async (code: string, method?: TwoFactorMethod, rememberDevice?: boolean) => {
    if (!twoFactorState) return;

    setIsVerifying(true);
    setTwoFactorError(null);

    try {
      // Pass the method, companyId and the remember-device opt-in to the API
      const response = await authService.verify2FA(
        twoFactorState.actualEmail,
        code,
        method || twoFactorState.requiredMethod,
        twoFactorState.selectedCompanyId,
        rememberDevice
      );

      if (response.success && response.token) {
        // Store the token first
        authService.setToken(response.token);

        // Complete login using the stored token (fetches user data from /auth/me)
        const user = await completeLoginWithToken();

        // Track successful 2FA verification
        track.auth('login', '2fa');
        track.event('2fa_verified', {
          user_role: user.role,
          company: user.company?.name || 'unknown',
          method: method || twoFactorState.requiredMethod,
          timestamp: new Date().toISOString(),
        });

        // Redirect to default route
        const defaultRoute = getDefaultRouteForRole(user.role);
        navigate(defaultRoute);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Código de verificación inválido';
      track.error('2fa_verification_failed', errorMessage);
      setTwoFactorError(errorMessage);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend2FA = async () => {
    if (!twoFactorState) return;

    try {
      await authService.resend2FA(twoFactorState.actualEmail);
      track.event('2fa_code_resent');
    } catch (err: unknown) {
      // Error handling is done in the TwoFactorVerification component
      throw err;
    }
  };

  const handleBackToLogin = () => {
    setStep('credentials');
    setCompanyState(null);
    setTwoFactorState(null);
    setTwoFactorError(null);
    setPassword(''); // Clear password for security
    setError('');
    track.event('login_flow_cancelled');
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
    track.buttonClick('toggle_password_visibility', 'login_form');
  };

  // AuthContext is still verifying a stored token — never flash the login
  // form while that's in flight. The redirect effect above takes over once
  // it resolves to an authenticated session.
  if (authLoading) {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Render based on current step
  if (step === 'blocked') {
    return <AuthenticatorRequiredScreen onBack={handleBackToLogin} />;
  }

  if (step === 'company-select' && companyState) {
    return (
      <CompanySelector
        email={companyState.email}
        companies={companyState.companies}
        onSelect={handleCompanySelect}
        onBack={handleBackToLogin}
        isLoading={isLoading}
      />
    );
  }

  if (step === '2fa' && twoFactorState) {
    return (
      <TwoFactorVerification
        maskedEmail={twoFactorState.maskedEmail}
        actualEmail={twoFactorState.actualEmail}
        expiresInMinutes={twoFactorState.expiresInMinutes || 10}
        onVerify={handleVerify2FA}
        onResend={handleResend2FA}
        onBack={handleBackToLogin}
        isVerifying={isVerifying}
        error={twoFactorError}
        // New single-method props
        requiredMethod={twoFactorState.requiredMethod}
        userHasTotp={twoFactorState.userHasTotp}
        // Grace period props
        isGracePeriod={twoFactorState.isGracePeriod}
        gracePeriodEndsAt={twoFactorState.gracePeriodEndsAt}
        canUseEmailDuringGrace={twoFactorState.canUseEmailDuringGrace}
        companyRequiredMethod={twoFactorState.companyRequiredMethod}
        // Email error props
        emailError={twoFactorState.emailError}
        hasBackupCodes={twoFactorState.hasBackupCodes}
        // Legacy props for backwards compatibility
        availableMethods={[twoFactorState.requiredMethod]}
      />
    );
  }

  // Default: credentials form
  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 3,
        }}
      >
        {/* Centered Login Form */}
        <Card sx={{ width: '100%', maxWidth: 400 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <img
                src="/safety-logo.png"
                alt="Safety Logo"
                style={{
                  height: '80px',
                  objectFit: 'contain',
                  marginBottom: '16px',
                }}
              />
              <Typography variant="h5" align="center" fontWeight="bold">
                Inicio de Sesión
              </Typography>
              <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1 }}>
                Sistema de Gestión de Seguridad
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {registrationState?.message && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {registrationState.message}
              </Alert>
            )}

            <form onSubmit={handleSubmit} autoComplete="on" method="post">
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Correo Electrónico"
                name="email"
                type="email"
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: (theme) => theme.palette.background.primary,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: (theme) => theme.palette.background.primary,
                  },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: (theme) => theme.palette.background.primary,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: (theme) => theme.palette.background.primary,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        disabled={isLoading}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  bgcolor: (theme) => theme.palette.background.buttonBg,
                  color: (theme) => theme.palette.background.buttonText,
                  '&:hover': {
                    bgcolor: (theme) => theme.palette.background.buttonBg,
                    filter: 'brightness(0.9)',
                  }
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Iniciar Sesión'}
              </Button>

              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  underline="hover"
                  sx={{
                    fontSize: '0.875rem',
                    color: (theme) => theme.palette.background.primary,
                  }}
                >
                  ¿Olvidó su contraseña?
                </Link>
              </Box>

              {/* Self-registration disabled — users are created by admins */}
            </form>
          </CardContent>

          {/* Version Number */}
          <Box
            sx={{
              px: 2,
              pb: 2,
              textAlign: 'center',
              borderTop: '1px solid',
              borderColor: 'divider',
              pt: 1.5,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.7rem',
                letterSpacing: '0.5px',
              }}
            >
              v{APP_VERSION}
            </Typography>
          </Box>
        </Card>
      </Box>
    </Container>
  );
};
